/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";

/** Common Components */
import Buttons from "../../../components/ui/button/Buttons";
import CardSection from "../../../components/ui/card/CardSection";
import FormCreator from "../../../components/finalForms/FormCreator";
import { customerFormData } from "./config/Customer.data";

/** RTK Query */
import {
  useLazyGetAllCountryListQuery,
  useAddCustomerMutation,
  useUpdateCustomerMutation,
  useLazyGetByIdQuery,
} from "../../../app/services/customerAPI";

/** Common Services & Data Files */
import ToastService from "../../../services/toastService/ToastService";

/** Utilities */
import { decryptUrlData } from "../../../services/CryptoService";
import { mapDropdownOptions } from "../../../components/finalForms/libs/mapDropdownOptions";

const AddEditCustomer = (props) => {
  const userRef = useRef();
  const navigate = useNavigate();
  const { id } = useParams();
  const customerId = id ? decryptUrlData(id) : 0;

  const [customerForm, setCustomerForm] = useState(customerFormData);

  //** API Call's */
  const [
    add,
    {
      isLoading: isCustomerAdding,
      isSuccess: isCustomerAdded,
      data: addCustomerData,
    },
  ] = useAddCustomerMutation();

  const [
    update,
    {
      isLoading: isUpdateLoading,
      isSuccess: isUpdateSuccess,
      data: isUpdateData,
    },
  ] = useUpdateCustomerMutation();

  const [
    getById,
    {
      isLoading: isDetailsLoading,
      isFetching: isGetByIdFetching,
      isSuccess: isGetByIdSuccess,
      data: isGetByIdData,
    },
  ] = useLazyGetByIdQuery();

  const [
    getAllCountries,
    {
      isLoading: icCountriesLoading,
      isFetching: icCountriesFetching,
      isSuccess: isCountriesFetched,
      data: countriesData,
    },
  ] = useLazyGetAllCountryListQuery();

  //** Funcation's */
  const handleSubmit = () => {
    let Customerdata = userRef.current.getFormData();
    if (Customerdata && !customerId) {
      const requestData = {
        ...Customerdata,
        country: Customerdata.countryId && typeof Customerdata.countryId === 'object' ? Customerdata.countryId.value : Customerdata.countryId,
        customerLogo: Customerdata.customerLogo?.[0]?.fileName,
        base64File: Customerdata.customerLogo?.[0]?.base64Data,
        isActive: Customerdata.isActive || false,
      };
      add(requestData);
    } else if (customerId && Customerdata) {
      const RequstUpdateData = {
        customerId: customerId,
        customerName: Customerdata.customerName,
        companyName: Customerdata.companyName,
        companyWebUrl: Customerdata.companyWebUrl,
        country: Customerdata.countryId && typeof Customerdata.countryId === 'object' ? Customerdata.countryId.value : Customerdata.countryId,
        customerLogo: Customerdata.customerLogo
          ? typeof Customerdata.customerLogo === "object"
            ? Customerdata.customerLogo?.[0]?.fileName
            : Customerdata.customerLogo
          : null,
        base64File: Customerdata.customerLogo
          ? typeof Customerdata.customerLogo === "object"
            ? Customerdata.customerLogo?.[0]?.base64Data
            : Customerdata.base64File
          : null,
        address: Customerdata.address,
        city: Customerdata.city,
        state: Customerdata.state,
        primaryEmail: Customerdata.primaryEmail,
        primaryContactPhone: Customerdata.primaryContactPhone,
        isActive: Customerdata.isActive || false,
      };
      update(RequstUpdateData);
    }
  };

  const resetForm = () => {
    const formData = { ...customerFormData };
    formData.initialState = { ...customerFormData.initialState };
    setCustomerForm(formData);
  };

  const backToList = () => {
    navigate("/admin/Customers");
  };

  //** Use Effects */
  useEffect(() => {
    getAllCountries();
  }, [getAllCountries]);

  useEffect(() => {
    if (isCustomerAdded && addCustomerData) {
      if (addCustomerData.errorMessage.includes("exists")) {
        ToastService.warning(addCustomerData.errorMessage);
        return;
      } else {
        ToastService.success(addCustomerData.errorMessage);
        resetForm();
        backToList();
      }
    }
  }, [isCustomerAdded, addCustomerData]);

  useEffect(() => {
    if (isUpdateSuccess && isUpdateData) {
      backToList();
      ToastService.success(isUpdateData.errorMessage);
    }
  }, [isUpdateSuccess, isUpdateData]);

  useEffect(() => {
    if (isGetByIdSuccess && !isGetByIdFetching && isGetByIdData) {
      if (isGetByIdData) {
        let formData = { ...customerForm };
        formData.initialState = {
          ...customerForm.initialState,
          customerName: isGetByIdData.customerName,
          companyName: isGetByIdData.companyName,
          companyWebUrl: isGetByIdData.companyWebUrl,
          countryId: isGetByIdData.country,
          address: isGetByIdData.address,
          city: isGetByIdData.city,
          state: isGetByIdData.state,
          primaryEmail: isGetByIdData.primaryEmail,
          customerLogo: `data:image/png;base64,${isGetByIdData.customerLogoBase64}`,
          base64File: isGetByIdData.customerLogoBase64,
          primaryContactPhone: isGetByIdData.primaryContactPhone,
          isActive: isGetByIdData.isActive,
        };
        setCustomerForm(formData);
      }
    }
  }, [isGetByIdSuccess, isGetByIdFetching, isGetByIdData]);

  useEffect(() => {
    resetForm();
    if (customerId) {
      getById(customerId);
    }
  }, [customerId]);

  useEffect(() => {
    if (!icCountriesFetching && isCountriesFetched && countriesData) {
      mapDropdownOptions(
        customerForm,
        "customer",
        "countryId",
        countriesData,
        "countryId",
        "countryName"
      );
    }
  }, [icCountriesFetching, isCountriesFetched, countriesData]);

  return (
    <div className="center-top-space">
      <CardSection
        isContentLoading={
          isCustomerAdding ||
          isUpdateLoading ||
          isDetailsLoading ||
          icCountriesLoading
        }
        cardTitle={"Customer Information"}
        iconName="ri:profile-line"
      >
        <div className="row">
          <FormCreator config={customerForm} ref={userRef} {...customerForm} />
          <div className="d-flex justify-content-end">
            <Buttons
              buttonTypeClassName="dark-btn"
              buttonText="Cancel"
              onClick={backToList}
            />
            <Buttons
              buttonTypeClassName="theme-btn ml-4"
              buttonText={`${customerId ? "Update" : "Add"}`}
              onClick={handleSubmit}
              isLoading={isCustomerAdding || isUpdateLoading}
            />
          </div>
        </div>
      </CardSection>
    </div>
  );
};

AddEditCustomer.propTypes = {
  initData: PropTypes.shape({
    customerName: PropTypes.string,
    companyName: PropTypes.string,
    companyWebUrl: PropTypes.string,
    country: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    primaryEmail: PropTypes.string,
    customerLogo: PropTypes.string,
    primaryContactPhone: PropTypes.string,
    isActive: PropTypes.bool,
  }),
};

export default AddEditCustomer;
